import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Seo from "../Seo";
import { reviewsBusinessJsonLd, breadcrumbJsonLd } from "../../seoSchema";
import "../../styles/Reviews.css";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import {
  cmsHeroBackgroundStyle,
  resolveFirestoreImage,
  useCmsHeroImageReady,
} from "../../firestoreMedia";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: "wishuponaplayhouse.firebaseapp.com",
  projectId: "wishuponaplayhouse",
  storageBucket: "wishuponaplayhouse.firebasestorage.app",
  messagingSenderId: "513377368862",
  appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
  measurementId: "G-NZ116LS0Q1",
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

const EMPTY = { name: "", location: "", comment: "", rating: 5 };

/* ── Helpers ── */
const Stars = ({ rating = 5, size = "sm" }) => (
  <div className={`rv-stars rv-stars--${size}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        className={i <= Math.round(rating) ? "rv-star rv-star--on" : "rv-star"}
      >
        ★
      </span>
    ))}
  </div>
);

const StarPicker = ({ value, onChange }) => (
  <div className="rv-star-picker">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        className={i <= value ? "rv-star rv-star--on" : "rv-star"}
        onClick={() => onChange(i)}
      >
        ★
      </button>
    ))}
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [filterStar, setFilterStar] = useState(0); // 0 = all
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, "Pages", "ReviewsPage"));
        if (snap.exists()) {
          const d = snap.data();
          const raw = d.reviewsData || [];
          setReviews(
            raw.map((r, i) => ({
              id: r.id ?? i,
              name: r.name || "Anonymous",
              location: r.location || (r.age ? `Age: ${r.age}` : ""),
              comment: r.comment || "",
              rating: r.rating ?? 5,
            })),
          );
          if (d.heroImage) setHeroImage(d.heroImage);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (updated) => {
    await updateDoc(doc(firestore, "Pages", "ReviewsPage"), {
      reviewsData: updated,
    });
    setReviews(updated);
  };

  /* ── derived stats ── */
  const avg = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  }, [reviews]);

  const countByStar = useMemo(() => {
    const c = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach((r) => {
      c[Math.round(r.rating)] = (c[Math.round(r.rating)] || 0) + 1;
    });
    return c;
  }, [reviews]);

  const filtered = useMemo(
    () =>
      filterStar === 0
        ? reviews
        : reviews.filter((r) => Math.round(r.rating) === filterStar),
    [reviews, filterStar],
  );

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const entry = {
        id: Date.now(),
        ...newReview,
        name: newReview.name.trim(),
        location: newReview.location.trim(),
        comment: newReview.comment.trim(),
      };
      await save([...reviews, entry]);
      setNewReview(EMPTY);
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const rvHeroUrl = !loading
    ? resolveFirestoreImage(heroImage, "/images/cottage.png")
    : "";
  const rvHeroReady = useCmsHeroImageReady(rvHeroUrl, !loading);

  const reviewsSchema = useMemo(() => reviewsBusinessJsonLd(reviews), [reviews]);
  const reviewsBreadcrumb = useMemo(
    () =>
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Reviews", path: "/reviews" },
      ]),
    [],
  );

  return (
    <div className="rv-page">
      <Seo
        title="Customer Reviews — Wish Upon A Playhouse | Denver, CO"
        description="Read reviews from Denver and Colorado families who trusted Wish Upon A Playhouse to build their dream custom children's playhouse."
        path="/reviews"
        jsonLd={reviewsSchema}
        jsonLdId="reviews-aggregate"
      />
      <Seo jsonLd={reviewsBreadcrumb} jsonLdId="reviews-breadcrumb" />
      <Navbar />

      {/* ── HERO ── */}
      <div className="sub-hero">
        <div
          className={`sub-hero-bg${loading ? " cms-hero-pending-fill" : ""}`}
          style={{
            ...cmsHeroBackgroundStyle(
              loading,
              heroImage,
              "/images/cottage.png",
            ),
            opacity: loading ? 1 : rvHeroReady ? 1 : 0,
            transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
        <div className="sub-hero-overlay" />
        <div className="sub-hero-content">
          <p className="sub-hero-eyebrow">Happy Families</p>
          <h1 className="sub-hero-title">Customer Reviews</h1>
        </div>
      </div>

      {/* ── SUMMARY BAR ── */}
      {!loading && reviews.length > 0 && (
        <div className="rv-summary">
          <div className="rv-summary-score">
            <span className="rv-avg-number">{avg.toFixed(1)}</span>
            <Stars rating={avg} size="lg" />
            <span className="rv-total-count">
              {reviews.length} review{reviews.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="rv-summary-bars">
            {[5, 4, 3, 2, 1].map((star) => (
              <button
                key={star}
                className={`rv-bar-row${filterStar === star ? " rv-bar-row--active" : ""}`}
                onClick={() => setFilterStar(filterStar === star ? 0 : star)}
                title={`Filter ${star} star`}
              >
                <span className="rv-bar-label">{star}★</span>
                <div className="rv-bar-track">
                  <div
                    className="rv-bar-fill"
                    style={{
                      width: reviews.length
                        ? `${(countByStar[star] / reviews.length) * 100}%`
                        : "0%",
                    }}
                  />
                </div>
                <span className="rv-bar-count">{countByStar[star]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── ACTIONS ── */}
      <div className="rv-actions-bar">
        <div className="rv-filter-pills">
          <button
            className={`rv-pill${filterStar === 0 ? " rv-pill--active" : ""}`}
            onClick={() => setFilterStar(0)}
          >
            All
          </button>
          {[5, 4, 3, 2, 1].map((s) => (
            <button
              key={s}
              className={`rv-pill${filterStar === s ? " rv-pill--active" : ""}`}
              onClick={() => setFilterStar(filterStar === s ? 0 : s)}
            >
              {s}★
            </button>
          ))}
        </div>
        <button
          className="rv-toggle-btn"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? "✕ Cancel" : "+ Share Your Review"}
        </button>
      </div>

      {/* ── FORM ── */}
      {showForm && (
        <div className="rv-form-wrap">
          <form className="rv-form" onSubmit={handleSubmit}>
            <h3 className="rv-form-title">Share Your Experience</h3>
            <div className="rv-form-row">
              <div className="rv-form-field">
                <label>Your Name *</label>
                <input
                  required
                  value={newReview.name}
                  onChange={(e) =>
                    setNewReview({ ...newReview, name: e.target.value })
                  }
                  placeholder="e.g. Sarah M."
                />
              </div>
              <div className="rv-form-field">
                <label>Location</label>
                <input
                  value={newReview.location}
                  onChange={(e) =>
                    setNewReview({ ...newReview, location: e.target.value })
                  }
                  placeholder="e.g. Denver, CO"
                />
              </div>
            </div>
            <div className="rv-form-field">
              <label>Rating</label>
              <StarPicker
                value={newReview.rating}
                onChange={(v) => setNewReview({ ...newReview, rating: v })}
              />
            </div>
            <div className="rv-form-field">
              <label>Your Review *</label>
              <textarea
                required
                rows={4}
                value={newReview.comment}
                onChange={(e) =>
                  setNewReview({ ...newReview, comment: e.target.value })
                }
                placeholder="Tell us about your experience…"
              />
            </div>
            <div className="rv-form-actions">
              <button
                type="submit"
                className="rv-submit-btn"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Review"}
              </button>
              <button
                type="button"
                className="rv-cancel-btn"
                onClick={() => {
                  setShowForm(false);
                  setNewReview(EMPTY);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── MASONRY GRID ── */}
      <div className="rv-grid-wrap">
        {loading ? (
          <p className="rv-empty">Loading reviews…</p>
        ) : filtered.length === 0 ? (
          <p className="rv-empty">
            {filterStar
              ? `No ${filterStar}-star reviews yet.`
              : "No reviews yet — be the first!"}
          </p>
        ) : (
          <div className="rv-masonry">
            {filtered.map((review) => (
              <div key={review.id} className="rv-card">
                <Stars rating={review.rating} size="sm" />
                <p className="rv-comment">"{review.comment}"</p>
                <div className="rv-card-footer">
                  <span className="rv-name">{review.name}</span>
                  {review.location && (
                    <span className="rv-location">{review.location}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Reviews;
